/**
 * TripDetailsView - Full-screen trip detail with overview cards and metrics.
 */

import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Refresh,
  Edit,
  LocationOn,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { getTrip, getTripRisks, getTripSummary, updateTripStatus } from "../services/tripService";
import { fetchByInternalTripId } from "../../publishedTrips/services/publishedTripsService.js";
import TripTimeline from "../components/TripTimeline";
import TripRiskSignals from "../components/TripRiskSignals";
import TripMap from "../components/TripMap";
import EditTripModal from "../components/EditTripModal";
import TripOrderAssignmentPanel from "../components/TripOrderAssignmentPanel";
import TripSharedLoadPanel from "../components/TripSharedLoadPanel";
import TripStatusBadge from "../components/TripStatusBadge.jsx";
import TripPastDueBadge from "../components/TripPastDueBadge.jsx";
import TripQuickSummaryBar from "../components/TripQuickSummaryBar.jsx";
import TripProgressBar from "../components/TripProgressBar.jsx";
import CapacityDonut from "../components/CapacityDonut.jsx";
import RouteTimeline from "../components/RouteTimeline.jsx";
import TripMetricsBar from "../components/TripMetricsBar.jsx";
import { logError, logInfo } from "../../../shared/utils/logger.js";
import { ACCEPTING_LOAD_TRIP_STATUS, ADVANCE_TRIP_STATUS_LABELS, getNextTripStatus } from "../constants/tripStatuses.js";
import {
  formatCapacityFraction,
  formatRouteDescription,
  formatTripDeparture,
  formatTripLoadSize,
  getAvgCapacityPerOrder,
  getCapacityPercent,
  getOrderCount,
  isPublishedOnPwa,
} from "../utils/tripFormatters.js";

const ACCENT = "#e3292f";
const PRIMARY = "#005249";

const Page = styled.div`
  padding: 24px 16px 40px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Breadcrumbs = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #64748b;
`;

const CrumbLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #64748b;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${PRIMARY};
    text-decoration: underline;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const HeaderMain = styled.div``;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #111827;
`;

const TripMeta = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  color: #64748b;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const DarkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #111827;
  color: #fff;

  &:hover {
    background: #1f2937;
  }
`;

const OutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #fff;
  color: #374151;

  &:hover {
    background: #f8fafc;
  }
`;

const TabsBar = styled.div`
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
  overflow-x: auto;
`;

const TabButton = styled.button`
  border: none;
  background: none;
  padding: 0 0 12px;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active }) => ($active ? "#111827" : "#64748b")};
  cursor: pointer;
  border-bottom: 3px solid ${({ $active }) => ($active ? ACCENT : "transparent")};
  margin-bottom: -1px;
  white-space: nowrap;

  &:hover {
    color: #111827;
  }
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const CardTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const InfoRow = styled.div``;

const InfoLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const VisibilityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #059669;
`;

const VisibilityDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
`;

const MapPanel = styled.div`
  height: 600px;
  min-height: 400px;
`;

const SharedLoadSection = styled.div`
  margin-top: 24px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;
`;

const TripDetailsView = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [summary, setSummary] = useState(null);
  const [risks, setRisks] = useState([]);
  const [publishedTrip, setPublishedTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState(null);

  const fetchTripDetails = useCallback(
    async (options = {}) => {
      const { silent = false } = options;
      if (!tripId) return;

      try {
        if (!silent) setLoading(true);
        setError(null);

        const [tripData, summaryData, risksData, published] = await Promise.all([
          getTrip(tripId),
          getTripSummary(tripId).catch(() => null),
          getTripRisks(tripId).catch(() => []),
          fetchByInternalTripId(tripId).catch(() => null),
        ]);

        setTrip(tripData);
        setSummary(summaryData);
        setRisks(risksData || []);
        setPublishedTrip(published);

        logInfo("Trip details fetched successfully", { tripId });
      } catch (err) {
        logError("Failed to fetch trip details", {
          tripId,
          error: err.message,
        });
        setError(err.message || "Failed to load trip details");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [tripId]
  );

  useEffect(() => {
    fetchTripDetails();
  }, [fetchTripDetails]);

  const handleAdvanceStatus = async () => {
    if (!tripId || !trip) return;

    const nextStatus = getNextTripStatus(trip.status);
    if (!nextStatus) return;

    try {
      setStatusUpdating(true);
      setStatusError(null);
      await updateTripStatus(tripId, nextStatus);
      await fetchTripDetails({ silent: true });
    } catch (err) {
      logError("Failed to advance trip status", {
        tripId,
        from: trip.status,
        to: nextStatus,
        error: err.message,
      });
      setStatusError(err.message || "Failed to update trip status");
    } finally {
      setStatusUpdating(false);
    }
  };

  const orderCount = summary?.ordersCount ?? getOrderCount(trip);
  const capacityPercent = getCapacityPercent(summary || trip);
  const capacityUsed = summary?.capacityUsed ?? trip?.capacityUsed ?? 0;
  const capacityTotal = summary?.capacityTotal ?? trip?.capacityTotal ?? 0;
  const avgPerOrder = getAvgCapacityPerOrder({
    ...trip,
    capacityUsed,
    orderCount,
  });

  if (loading) {
    return (
      <Page>
        <LoadingWrap>
          <CircularProgress />
        </LoadingWrap>
      </Page>
    );
  }

  if (error || !trip) {
    return (
      <Page>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || "Trip not found"}
        </Alert>
        <OutlineButton type="button" onClick={() => navigate("/trips")}>
          Back to Trips
        </OutlineButton>
      </Page>
    );
  }

  const nextTripStatus = getNextTripStatus(trip.status);
  const advanceStatusLabel = nextTripStatus
    ? ADVANCE_TRIP_STATUS_LABELS[nextTripStatus]
    : null;

  return (
    <Page>
      <Breadcrumbs>
        <CrumbLink type="button" onClick={() => navigate("/trips")}>
          Trips
        </CrumbLink>
        <span>/</span>
        <span>Trip Details</span>
      </Breadcrumbs>

      {trip.isDepartureOverdue ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This planned trip&apos;s departure date has passed. Reschedule the departure
          to clear past-due, or mark completed when ops closes the trip.
        </Alert>
      ) : null}

      {statusError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStatusError(null)}>
          {statusError}
        </Alert>
      ) : null}

      <HeaderRow>
        <HeaderMain>
          <TitleRow>
            <Title>{trip.routeCorridor}</Title>
            <TripStatusBadge status={trip.status} size="medium" />
            {trip.isDepartureOverdue ? <TripPastDueBadge /> : null}
          </TitleRow>
          <TripMeta>
            Trip ID: <strong>{trip.id}</strong>
          </TripMeta>
        </HeaderMain>

        <HeaderActions>
          <Tooltip title="Refresh trip data">
            <IconButton
              onClick={() => fetchTripDetails()}
              sx={{ border: 1, borderColor: "divider" }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <DarkButton
            type="button"
            onClick={() => navigate(`/trips/${tripId}/drops`)}
          >
            <LocationOn fontSize="small" /> Manage Drops
          </DarkButton>
          {trip.status === ACCEPTING_LOAD_TRIP_STATUS ? (
            <DarkButton type="button" onClick={() => setScheduleModalOpen(true)}>
              Schedule departure
            </DarkButton>
          ) : null}
          {advanceStatusLabel ? (
            <DarkButton
              type="button"
              onClick={handleAdvanceStatus}
              disabled={statusUpdating}
            >
              {statusUpdating ? "Updating…" : advanceStatusLabel}
            </DarkButton>
          ) : null}
          <OutlineButton type="button" onClick={() => setEditModalOpen(true)}>
            <Edit fontSize="small" /> Edit Trip
          </OutlineButton>
        </HeaderActions>
      </HeaderRow>

      <TripQuickSummaryBar trip={trip} summary={summary} />

      <TabsBar>
        {[
          { key: "overview", label: "Overview" },
          { key: "map", label: "Map" },
          { key: "timeline", label: "Timeline" },
          { key: "risks", label: "Risks" },
          { key: "orders", label: `Orders (${orderCount})` },
        ].map((tab) => (
          <TabButton
            key={tab.key}
            type="button"
            $active={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabsBar>

      {activeTab === "overview" ? (
        <>
          <OverviewGrid>
            <Card>
              <CardTitle>Trip Information</CardTitle>
              <InfoList>
                <InfoRow>
                  <InfoLabel>Status</InfoLabel>
                  <InfoValue>{trip.status?.toUpperCase().replace("_", " ") || "PLANNED"}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Route Corridor</InfoLabel>
                  <InfoValue>{formatRouteDescription(trip.routeCorridor)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Departure Date</InfoLabel>
                  <InfoValue>{formatTripDeparture(trip.departureDate)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Load size</InfoLabel>
                  <InfoValue>{formatTripLoadSize(trip)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Capacity Used</InfoLabel>
                  <InfoValue>{formatCapacityFraction(summary || trip)}</InfoValue>
                  <div style={{ marginTop: 8 }}>
                    <TripProgressBar value={capacityPercent} />
                  </div>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Visibility</InfoLabel>
                  {isPublishedOnPwa(publishedTrip) ? (
                    <VisibilityRow>
                      <VisibilityDot />
                      Published on PWA
                    </VisibilityRow>
                  ) : (
                    <InfoValue style={{ color: "#64748b" }}>Not on PWA</InfoValue>
                  )}
                </InfoRow>
              </InfoList>
            </Card>

            <Card>
              <CardTitle>Route Summary</CardTitle>
              <RouteTimeline
                corridor={trip.routeCorridor}
                onViewMap={() => setActiveTab("map")}
              />
            </Card>

            <Card>
              <CardTitle>Capacity &amp; Utilisation</CardTitle>
              <CapacityDonut
                percent={capacityPercent}
                usedM3={capacityUsed}
                totalM3={capacityTotal}
                avgPerOrder={avgPerOrder}
              />
            </Card>
          </OverviewGrid>

          <TripMetricsBar
            ordersCount={summary?.ordersCount ?? orderCount}
            dropsCount={summary?.dropsCount ?? trip.drops?.length ?? 0}
            capacityUsedM3={capacityUsed}
            estimatedRevenue={summary?.estimatedRevenue ?? 0}
            riskSummary={summary?.riskSummary}
          />

          <SharedLoadSection>
            <TripSharedLoadPanel
              internalTripId={trip.id}
              routeCorridor={trip.routeCorridor}
              departureDate={trip.departureDate}
            />
          </SharedLoadSection>
        </>
      ) : null}

      {activeTab === "map" ? (
        <MapPanel>
          <TripMap
            trip={trip}
            drops={trip.drops || []}
            orders={trip.orders || []}
            onDropClick={(drop) => {
              logInfo("Drop clicked", { dropId: drop.id });
            }}
          />
        </MapPanel>
      ) : null}

      {activeTab === "timeline" ? (
        <TripTimeline drops={trip.drops || []} orders={trip.orders || []} />
      ) : null}

      {activeTab === "risks" ? <TripRiskSignals risks={risks} /> : null}

      {activeTab === "orders" ? (
        <TripOrderAssignmentPanel
          trip={trip}
          onOrdersChanged={() => fetchTripDetails({ silent: true })}
        />
      ) : null}

      <EditTripModal
        open={editModalOpen}
        trip={trip}
        onSuccess={() => {
          setEditModalOpen(false);
          fetchTripDetails();
        }}
        onCancel={() => setEditModalOpen(false)}
      />
      <EditTripModal
        open={scheduleModalOpen}
        trip={trip}
        scheduleMode
        onSuccess={() => {
          setScheduleModalOpen(false);
          fetchTripDetails();
        }}
        onCancel={() => setScheduleModalOpen(false)}
      />
    </Page>
  );
};

export default TripDetailsView;
